import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Sparkles, 
  RefreshCw, 
  Copy, 
  Check,
  Mail,
  MessageCircle,
  Clock,
  Download,
  Phone,
  Wand2,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ChevronRight,
  GripVertical,
  Plus,
  Trash2,
  Calendar,
  Lock,
  LockOpen,
  X,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { getVariantById } from '@/config/funnelStrategies';

// ==================== CLEAN INTERNAL LABELS ====================
const cleanContentLabels = (content) => {
  if (!content) return '';
  
  // Lista de etiquetas internas a eliminar
  const internalLabels = [
    'HEADLINES?:', 'HEADLINE:', 'SUBHEADLINE:', 'SUB-HEADLINE:',
    'INTRODUCCIÓN:', 'INTRODUCCION:', 'INTRODUCTION:',
    'CTA:', 'CALL TO ACTION:', 'LLAMADA A LA ACCIÓN:',
    'TESTIMONIOS?:', 'TESTIMONIO:', 'PRUEBA SOCIAL:',
    'CUERPO:', 'BODY:', 'CONTENIDO:', 'CONTENT:',
    'ASUNTO:', 'SUBJECT:', 'PREHEADER:', 'PRE-HEADER:',
    'URGENCIA:', 'BENEFICIOS?:', 'BENEFICIO:',
    'OFERTA:', 'GARANTÍA:', 'GARANTIA:',
    'P\\.S\\.:', 'PS:', 'POSDATA:',
    'APERTURA:', 'CIERRE:', 'FIRMA:'
  ];
  
  let cleaned = content;
  
  // Elimina las etiquetas y sus emojis/símbolos precedentes
  internalLabels.forEach(label => {
    const regex = new RegExp(
      `[🎯📢🔥💡✅❌🚀💰🎁📌🔑⚡️•\\-\\*]?\\s*${label}\\s*`, 
      'gim'
    );
    cleaned = cleaned.replace(regex, '');
  });
  
  // Elimina líneas que son SOLO mayúsculas y dos puntos (etiquetas no listadas)
  cleaned = cleaned.replace(/^[A-ZÁÉÍÓÚÑ\s]{3,}:\s*$/gm, '');
  
  // Elimina líneas vacías múltiples consecutivas (más de 2)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Elimina asteriscos de markdown
  cleaned = cleaned
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1');
  
  return cleaned.trim();
};

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ==================== SORTABLE SEQUENCE ITEM ====================
const SortableSequenceItem = ({ 
  id, 
  name, 
  index, 
  isActive, 
  hasContent, 
  isPredefined,
  isGeneratingAll,
  onSelect,
  onDelete,
  getStatusIcon,
  type, // 'email' or 'whatsapp'
  momento // Momento de envío (ej: "Inmediato tras registro")
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const activeColor = type === 'whatsapp' ? '[#25D366]' : 'brand';
  const activeBgColor = type === 'whatsapp' ? 'bg-[#25D366]/10' : 'bg-brand/10';
  const activeBorderColor = type === 'whatsapp' ? 'border-[#25D366]' : 'border-brand';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-3 rounded-lg text-left transition-all ${
        isActive
          ? `${activeBgColor} border-2 ${activeBorderColor}`
          : hasContent
            ? 'bg-surface border-2 border-[#00D68F]/30'
            : 'bg-surface border-2 border-transparent hover:border-border-subtle'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/10 rounded touch-none"
        data-testid={`drag-handle-${type}-${index}`}
      >
        <GripVertical className="w-4 h-4 text-text-muted" />
      </button>

      {/* Main clickable area */}
      <button
        onClick={() => onSelect(index)}
        disabled={isGeneratingAll}
        className="flex-1 flex items-center gap-3 text-left"
        data-testid={`${type}-item-${index}`}
      >
        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${
          isActive 
            ? type === 'whatsapp' ? 'bg-[#25D366] text-white' : 'bg-brand-500 text-white'
            : hasContent 
              ? 'bg-[#00D68F]/20 text-[#00D68F]' 
              : 'bg-app-bg text-text-muted'
        }`}>
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm truncate ${
              isActive 
                ? type === 'whatsapp' ? 'text-[#25D366] font-medium' : 'text-brand-500 font-medium'
                : hasContent ? 'text-text-main' : 'text-text-secondary'
            }`}>
              {name}
            </p>
            {/* Predefined indicator */}
            {isPredefined && (
              <span 
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${type === 'whatsapp' ? 'bg-[#25D366]/60' : 'bg-brand/60'}`}
                title="Pieza predefinida por la estrategia"
              />
            )}
          </div>
          {/* Momento de envío */}
          {momento && (
            <p className="text-[10px] text-text-muted mt-0.5 flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" />
              {momento}
            </p>
          )}
        </div>
        {getStatusIcon(index, hasContent)}
        {isActive && !hasContent && <ChevronRight className={`w-4 h-4 ${type === 'whatsapp' ? 'text-[#25D366]' : 'text-brand'}`} />}
      </button>

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(name, index, isPredefined);
        }}
        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded transition-all"
        title={isPredefined ? "Eliminar pieza predefinida" : "Eliminar pieza personalizada"}
        data-testid={`delete-${type}-${index}`}
      >
        <Trash2 className="w-3.5 h-3.5 text-red-400" />
      </button>
    </div>
  );
};


const SequencesStep = ({ funnel, brief, emailAssets, whatsappAssets, assetsConfig, onEmailsUpdate, onWhatsappUpdate, onAssetsConfigUpdate, onEmergencySave }) => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('emails');
  const [generatingEmails, setGeneratingEmails] = useState(false);
  const [generatingWhatsapp, setGeneratingWhatsapp] = useState(false);
  const [generatingAllEmails, setGeneratingAllEmails] = useState(false);
  const [generatingAllWhatsapp, setGeneratingAllWhatsapp] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);
  const [emergencySaving, setEmergencySaving] = useState(false);
  
  // Track individual email/whatsapp generation status
  const [emailProgress, setEmailProgress] = useState({});
  const [whatsappProgress, setWhatsappProgress] = useState({});
  
  // Generated content for each message - Initialize from props OR localStorage
  // CRITICAL: Only load from localStorage if we have a valid funnel.id
  const [emailContent, setEmailContent] = useState(() => {
    // Try to load from props first
    if (emailAssets && emailAssets.length > 0) {
      const contentMap = {};
      emailAssets.forEach((asset, index) => {
        if (asset.content?.copy) {
          contentMap[index] = asset.content.copy;
        }
      });
      if (Object.keys(contentMap).length > 0) return contentMap;
    }
    // Fallback to localStorage ONLY if we have a valid funnel ID
    if (funnel?.id) {
      const saved = localStorage.getItem(`sequences_emails_${funnel.id}`);
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });
  
  const [whatsappContent, setWhatsappContent] = useState(() => {
    // Try to load from props first
    if (whatsappAssets && whatsappAssets.length > 0) {
      const contentMap = {};
      whatsappAssets.forEach((asset, index) => {
        if (asset.content?.copy) {
          contentMap[index] = asset.content.copy;
        }
      });
      if (Object.keys(contentMap).length > 0) return contentMap;
    }
    // Fallback to localStorage ONLY if we have a valid funnel ID
    if (funnel?.id) {
      const saved = localStorage.getItem(`sequences_whatsapp_${funnel.id}`);
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });
  
  // Active item in each tab
  const [activeEmail, setActiveEmail] = useState(0);
  const [activeWhatsapp, setActiveWhatsapp] = useState(0);
  
  // Approval states for emails and whatsapp
  const [approvedEmails, setApprovedEmails] = useState(() => {
    if (funnel?.id) {
      const saved = localStorage.getItem(`sequences_approved_emails_${funnel.id}`);
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });
  
  const [approvedWhatsapp, setApprovedWhatsapp] = useState(() => {
    if (funnel?.id) {
      const saved = localStorage.getItem(`sequences_approved_whatsapp_${funnel.id}`);
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });
  
  // Editing states - when true, shows textarea for manual editing
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingWhatsapp, setEditingWhatsapp] = useState(null);
  const [editedEmailContent, setEditedEmailContent] = useState('');
  const [editedWhatsappContent, setEditedWhatsappContent] = useState('');
  
  // Local funnel ID reference (will be updated by emergency save if needed)
  const [localFunnelId, setLocalFunnelId] = useState(funnel?.id);

  // Update local funnel ID when prop changes
  useEffect(() => {
    if (funnel?.id) {
      setLocalFunnelId(funnel.id);
    }
  }, [funnel?.id]);

  // CRITICAL: Load saved assets from MongoDB on mount
  // This ensures content persists across sessions/devices
  useEffect(() => {
    const loadSavedAssets = async () => {
      if (!funnel?.id) return;
      
      try {
        const response = await api.get(`/funnels/${funnel.id}/assets`);
        const assets = response.data || [];
        
        // Process email assets
        const emailMap = {};
        const waMap = {};
        
        assets.forEach(asset => {
          if (asset.asset_type === 'email' && asset.content?.copy) {
            const index = (asset.content?.email_index || 1) - 1;
            emailMap[index] = asset.content.copy;
          } else if (asset.asset_type === 'whatsapp' && asset.content?.copy) {
            const index = (asset.content?.message_index || 1) - 1;
            waMap[index] = asset.content.copy;
          }
        });
        
        // Only update if we found saved assets (don't overwrite localStorage data with empty)
        if (Object.keys(emailMap).length > 0) {
          setEmailContent(prev => ({ ...prev, ...emailMap }));
          // Also update progress to show as completed
          const emailProgress = {};
          Object.keys(emailMap).forEach(idx => {
            emailProgress[idx] = 'completed';
          });
          setEmailProgress(prev => ({ ...prev, ...emailProgress }));
        }
        
        if (Object.keys(waMap).length > 0) {
          setWhatsappContent(prev => ({ ...prev, ...waMap }));
          // Also update progress to show as completed
          const waProgress = {};
          Object.keys(waMap).forEach(idx => {
            waProgress[idx] = 'completed';
          });
          setWhatsappProgress(prev => ({ ...prev, ...waProgress }));
        }
      } catch (err) {
        console.log('Could not load saved assets:', err.message);
        // Not critical - will use localStorage fallback
      }
    };
    
    loadSavedAssets();
  }, [funnel?.id, api]);

  // Emergency save function - ensures funnel exists before generation
  const ensureFunnelExists = async () => {
    // First check prop funnel.id (most reliable source)
    if (funnel?.id) {
      setLocalFunnelId(funnel.id);
      return funnel.id;
    }
    
    // Then check our local cached ID
    if (localFunnelId) return localFunnelId;
    
    // Try emergency save
    if (onEmergencySave) {
      setEmergencySaving(true);
      try {
        const savedFunnel = await onEmergencySave();
        if (savedFunnel?.id) {
          setLocalFunnelId(savedFunnel.id);
          toast.success('Funnel sincronizado', { duration: 2000 });
          return savedFunnel.id;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[SequencesStep] Emergency save failed:', error);
        }
      } finally {
        setEmergencySaving(false);
      }
    }
    
    // If we still don't have an ID, show error and return null
    toast.error('Por favor, completa los pasos anteriores primero', {
      description: 'Necesitas seleccionar cliente y nombre del funnel'
    });
    return null;
  };

  // Get counts from assetsConfig (SOURCE OF TRUTH)
  const totalEmails = assetsConfig?.emails || 8;
  const totalWhatsapp = assetsConfig?.whatsapp || 5;

  // Check if strategy has emails/whatsapp configured
  const strategyVariant = useMemo(() => {
    return getVariantById(funnel?.strategy, funnel?.strategy_variant);
  }, [funnel?.strategy, funnel?.strategy_variant]);
  
  const strategyHasEmails = useMemo(() => {
    return strategyVariant?.emails?.length > 0;
  }, [strategyVariant]);
  
  const strategyHasWhatsapp = useMemo(() => {
    return strategyVariant?.whatsapp?.length > 0;
  }, [strategyVariant]);
  
  // Check if user has manually added emails/whatsapp (overriding strategy default)
  const userAddedEmails = totalEmails > 0 && !strategyHasEmails;
  const userAddedWhatsapp = totalWhatsapp > 0 && !strategyHasWhatsapp;

  // Generate email names based on count
  const generateEmailNames = (count) => {
    const names = [];
    const preCount = Math.ceil(count * 0.4); // 40% pre-evento
    const postCount = count - preCount;
    
    for (let i = 1; i <= preCount; i++) {
      if (i === 1) names.push('Email de Bienvenida');
      else if (i === 2) names.push('Recordatorio D-3');
      else if (i === 3) names.push('Recordatorio D-1');
      else if (i === 4) names.push('Email Día del Evento');
      else names.push(`Email Pre-Evento ${i}`);
    }
    
    for (let i = 1; i <= postCount; i++) {
      if (i === 1) names.push('Email Replay Disponible');
      else if (i === 2) names.push('Email Oferta + Testimonios');
      else if (i === 3) names.push('Email Urgencia 48h');
      else if (i === 4) names.push('Email Urgencia 24h');
      else if (i === 5) names.push('Email Última Oportunidad');
      else if (i === 6) names.push('Email Carrito Cerrado');
      else if (i === 7) names.push('Email Downsell');
      else names.push(`Email Nurturing ${i - 7}`);
    }
    
    return names.slice(0, count);
  };

  // Generate WhatsApp names based on count
  const generateWhatsappNames = (count) => {
    const names = [];
    const preCount = Math.ceil(count * 0.4);
    const postCount = count - preCount;
    
    for (let i = 1; i <= preCount; i++) {
      if (i === 1) names.push('WA Confirmación');
      else if (i === 2) names.push('WA Recordatorio D-1');
      else if (i === 3) names.push('WA 3h Antes');
      else if (i === 4) names.push('WA Link del Directo');
      else names.push(`WA Pre-Evento ${i}`);
    }
    
    for (let i = 1; i <= postCount; i++) {
      if (i === 1) names.push('WA Replay');
      else if (i === 2) names.push('WA Oferta Especial');
      else if (i === 3) names.push('WA Urgencia');
      else if (i === 4) names.push('WA Último Aviso');
      else if (i === 5) names.push('WA Gracias');
      else names.push(`WA Seguimiento ${i}`);
    }
    
    return names.slice(0, count);
  };

  const emailNames = generateEmailNames(totalEmails);
  const whatsappNames = generateWhatsappNames(totalWhatsapp);

  // ====== CUSTOM NAMES FROM ASSETS CONFIG ======
  // If user has customized names (reordered/added/removed), use those
  const customEmailNames = useMemo(() => {
    return assetsConfig?.emailNames || emailNames;
  }, [assetsConfig?.emailNames, emailNames]);

  const customWhatsappNames = useMemo(() => {
    return assetsConfig?.whatsappNames || whatsappNames;
  }, [assetsConfig?.whatsappNames, whatsappNames]);

  // Original predefined names for indicator
  const originalEmailNames = useMemo(() => {
    return assetsConfig?.originalEmailNames || emailNames;
  }, [assetsConfig?.originalEmailNames, emailNames]);

  const originalWhatsappNames = useMemo(() => {
    return assetsConfig?.originalWhatsappNames || whatsappNames;
  }, [assetsConfig?.originalWhatsappNames, whatsappNames]);

  // Check if item is predefined
  const isPredefinedEmail = useCallback((name) => originalEmailNames.includes(name), [originalEmailNames]);
  const isPredefinedWhatsapp = useCallback((name) => originalWhatsappNames.includes(name), [originalWhatsappNames]);

  // ====== GET WHATSAPP METADATA FROM STRATEGY ======
  const getWhatsappMetadata = useCallback((nombre) => {
    const variant = getVariantById(funnel?.strategy, funnel?.strategy_variant);
    return variant?.whatsapp?.find(w => w.nombre === nombre) || null;
  }, [funnel?.strategy, funnel?.strategy_variant]);

  // ====== GET EMAIL METADATA FROM STRATEGY ======
  const getEmailMetadata = useCallback((nombre) => {
    const variant = getVariantById(funnel?.strategy, funnel?.strategy_variant);
    return variant?.emails?.find(e => e.nombre === nombre) || null;
  }, [funnel?.strategy, funnel?.strategy_variant]);

  // ====== D&D STATES ======
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [showAddWhatsappModal, setShowAddWhatsappModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, name: null, index: null, isPredefined: false, type: null });

  // D&D Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle D&D reorder for emails
  const handleEmailDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = customEmailNames.findIndex((_, i) => `email-${i}` === active.id);
      const newIndex = customEmailNames.findIndex((_, i) => `email-${i}` === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(customEmailNames, oldIndex, newIndex);
        
        if (onAssetsConfigUpdate) {
          onAssetsConfigUpdate({
            ...assetsConfig,
            emailNames: newOrder,
            originalEmailNames: assetsConfig?.originalEmailNames || emailNames
          });
        }
        
        // Adjust active index
        if (activeEmail === oldIndex) setActiveEmail(newIndex);
        else if (oldIndex < activeEmail && newIndex >= activeEmail) setActiveEmail(activeEmail - 1);
        else if (oldIndex > activeEmail && newIndex <= activeEmail) setActiveEmail(activeEmail + 1);
        
        toast.success('Orden de emails actualizado');
      }
    }
  }, [customEmailNames, assetsConfig, onAssetsConfigUpdate, emailNames, activeEmail]);

  // Handle D&D reorder for whatsapp
  const handleWhatsappDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = customWhatsappNames.findIndex((_, i) => `whatsapp-${i}` === active.id);
      const newIndex = customWhatsappNames.findIndex((_, i) => `whatsapp-${i}` === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(customWhatsappNames, oldIndex, newIndex);
        
        if (onAssetsConfigUpdate) {
          onAssetsConfigUpdate({
            ...assetsConfig,
            whatsappNames: newOrder,
            originalWhatsappNames: assetsConfig?.originalWhatsappNames || whatsappNames
          });
        }
        
        if (activeWhatsapp === oldIndex) setActiveWhatsapp(newIndex);
        else if (oldIndex < activeWhatsapp && newIndex >= activeWhatsapp) setActiveWhatsapp(activeWhatsapp - 1);
        else if (oldIndex > activeWhatsapp && newIndex <= activeWhatsapp) setActiveWhatsapp(activeWhatsapp + 1);
        
        toast.success('Orden de WhatsApp actualizado');
      }
    }
  }, [customWhatsappNames, assetsConfig, onAssetsConfigUpdate, whatsappNames, activeWhatsapp]);

  // ====== APPROVAL FUNCTIONS ======
  const handleApproveEmail = useCallback((index) => {
    setApprovedEmails(prev => {
      const updated = { ...prev, [index]: true };
      if (funnel?.id) {
        localStorage.setItem(`sequences_approved_emails_${funnel.id}`, JSON.stringify(updated));
      }
      return updated;
    });
    toast.success('Email aprobado');
  }, [funnel?.id]);

  const handleUnapproveEmail = useCallback((index) => {
    setApprovedEmails(prev => {
      const updated = { ...prev };
      delete updated[index];
      if (funnel?.id) {
        localStorage.setItem(`sequences_approved_emails_${funnel.id}`, JSON.stringify(updated));
      }
      return updated;
    });
    toast.success('Email desbloqueado para edición');
  }, [funnel?.id]);

  const handleApproveWhatsapp = useCallback((index) => {
    setApprovedWhatsapp(prev => {
      const updated = { ...prev, [index]: true };
      if (funnel?.id) {
        localStorage.setItem(`sequences_approved_whatsapp_${funnel.id}`, JSON.stringify(updated));
      }
      return updated;
    });
    toast.success('WhatsApp aprobado');
  }, [funnel?.id]);

  const handleUnapproveWhatsapp = useCallback((index) => {
    setApprovedWhatsapp(prev => {
      const updated = { ...prev };
      delete updated[index];
      if (funnel?.id) {
        localStorage.setItem(`sequences_approved_whatsapp_${funnel.id}`, JSON.stringify(updated));
      }
      return updated;
    });
    toast.success('WhatsApp desbloqueado para edición');
  }, [funnel?.id]);

  // ====== APPROVE ALL FUNCTIONS ======
  const handleApproveAllEmails = useCallback(() => {
    const allApproved = {};
    Object.keys(emailContent).forEach((key, index) => {
      if (emailContent[key]) {
        allApproved[index] = true;
      }
    });
    setApprovedEmails(allApproved);
    if (funnel?.id) {
      localStorage.setItem(`sequences_approved_emails_${funnel.id}`, JSON.stringify(allApproved));
    }
    toast.success(`${Object.keys(allApproved).length} emails aprobados`);
  }, [emailContent, funnel?.id]);

  const handleUnapproveAllEmails = useCallback(() => {
    setApprovedEmails({});
    if (funnel?.id) {
      localStorage.setItem(`sequences_approved_emails_${funnel.id}`, JSON.stringify({}));
    }
    toast.success('Todos los emails desbloqueados');
  }, [funnel?.id]);

  const handleApproveAllWhatsapp = useCallback(() => {
    const allApproved = {};
    Object.keys(whatsappContent).forEach((key, index) => {
      if (whatsappContent[key]) {
        allApproved[index] = true;
      }
    });
    setApprovedWhatsapp(allApproved);
    if (funnel?.id) {
      localStorage.setItem(`sequences_approved_whatsapp_${funnel.id}`, JSON.stringify(allApproved));
    }
    toast.success(`${Object.keys(allApproved).length} WhatsApps aprobados`);
  }, [whatsappContent, funnel?.id]);

  const handleUnapproveAllWhatsapp = useCallback(() => {
    setApprovedWhatsapp({});
    if (funnel?.id) {
      localStorage.setItem(`sequences_approved_whatsapp_${funnel.id}`, JSON.stringify({}));
    }
    toast.success('Todos los WhatsApps desbloqueados');
  }, [funnel?.id]);

  // ==================== MANUAL EDITING HANDLERS ====================
  
  // Start editing email
  const handleStartEditingEmail = useCallback((index) => {
    setEditingEmail(index);
    setEditedEmailContent(emailContent[index] || '');
  }, [emailContent]);
  
  // Cancel editing email
  const handleCancelEditingEmail = useCallback(() => {
    setEditingEmail(null);
    setEditedEmailContent('');
  }, []);
  
  // Save edited email
  const handleSaveEditedEmail = useCallback((index) => {
    setEmailContent(prev => ({
      ...prev,
      [index]: editedEmailContent
    }));
    setEditingEmail(null);
    setEditedEmailContent('');
    toast.success('Email actualizado');
  }, [editedEmailContent]);
  
  // Start editing whatsapp
  const handleStartEditingWhatsapp = useCallback((index) => {
    setEditingWhatsapp(index);
    setEditedWhatsappContent(whatsappContent[index] || '');
  }, [whatsappContent]);
  
  // Cancel editing whatsapp
  const handleCancelEditingWhatsapp = useCallback(() => {
    setEditingWhatsapp(null);
    setEditedWhatsappContent('');
  }, []);
  
  // Save edited whatsapp
  const handleSaveEditedWhatsapp = useCallback((index) => {
    setWhatsappContent(prev => ({
      ...prev,
      [index]: editedWhatsappContent
    }));
    setEditingWhatsapp(null);
    setEditedWhatsappContent('');
    toast.success('WhatsApp actualizado');
  }, [editedWhatsappContent]);

  // Handle add new email
  const handleAddEmail = useCallback(() => {
    const trimmedName = newItemName.trim();
    if (!trimmedName) { toast.error('Introduce un nombre'); return; }
    if (customEmailNames.some(n => n.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error('Ya existe un email con ese nombre'); return;
    }
    
    const newNames = [...customEmailNames, trimmedName];
    if (onAssetsConfigUpdate) {
      onAssetsConfigUpdate({
        ...assetsConfig,
        emailNames: newNames,
        emails: newNames.length,
        originalEmailNames: assetsConfig?.originalEmailNames || emailNames
      });
    }
    
    setNewItemName('');
    setShowAddEmailModal(false);
    setActiveEmail(newNames.length - 1);
    toast.success(`"${trimmedName}" añadido`);
  }, [newItemName, customEmailNames, assetsConfig, onAssetsConfigUpdate, emailNames]);

  // Handle add new whatsapp
  const handleAddWhatsapp = useCallback(() => {
    const trimmedName = newItemName.trim();
    if (!trimmedName) { toast.error('Introduce un nombre'); return; }
    if (customWhatsappNames.some(n => n.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error('Ya existe un mensaje con ese nombre'); return;
    }
    
    const newNames = [...customWhatsappNames, trimmedName];
    if (onAssetsConfigUpdate) {
      onAssetsConfigUpdate({
        ...assetsConfig,
        whatsappNames: newNames,
        whatsapp: newNames.length,
        originalWhatsappNames: assetsConfig?.originalWhatsappNames || whatsappNames
      });
    }
    
    setNewItemName('');
    setShowAddWhatsappModal(false);
    setActiveWhatsapp(newNames.length - 1);
    toast.success(`"${trimmedName}" añadido`);
  }, [newItemName, customWhatsappNames, assetsConfig, onAssetsConfigUpdate, whatsappNames]);

  // Handle delete
  const handleDeleteItem = useCallback(() => {
    const { name, index, type } = deleteConfirm;
    const names = type === 'email' ? customEmailNames : customWhatsappNames;
    
    if (names.length <= 1) {
      toast.error('Debes tener al menos un elemento');
      setDeleteConfirm({ open: false, name: null, index: null, isPredefined: false, type: null });
      return;
    }
    
    const newNames = names.filter((_, i) => i !== index);
    
    // Remove content
    if (type === 'email') {
      const newContent = { ...emailContent };
      delete newContent[index];
      // Reindex content
      const reindexed = {};
      Object.keys(newContent).forEach(key => {
        const k = parseInt(key);
        if (k > index) reindexed[k - 1] = newContent[key];
        else reindexed[k] = newContent[key];
      });
      setEmailContent(reindexed);
      
      if (onAssetsConfigUpdate) {
        onAssetsConfigUpdate({
          ...assetsConfig,
          emailNames: newNames,
          emails: newNames.length,
          originalEmailNames: assetsConfig?.originalEmailNames || emailNames
        });
      }
      
      if (activeEmail >= newNames.length) setActiveEmail(Math.max(0, newNames.length - 1));
      else if (activeEmail > index) setActiveEmail(activeEmail - 1);
    } else {
      const newContent = { ...whatsappContent };
      delete newContent[index];
      const reindexed = {};
      Object.keys(newContent).forEach(key => {
        const k = parseInt(key);
        if (k > index) reindexed[k - 1] = newContent[key];
        else reindexed[k] = newContent[key];
      });
      setWhatsappContent(reindexed);
      
      if (onAssetsConfigUpdate) {
        onAssetsConfigUpdate({
          ...assetsConfig,
          whatsappNames: newNames,
          whatsapp: newNames.length,
          originalWhatsappNames: assetsConfig?.originalWhatsappNames || whatsappNames
        });
      }
      
      if (activeWhatsapp >= newNames.length) setActiveWhatsapp(Math.max(0, newNames.length - 1));
      else if (activeWhatsapp > index) setActiveWhatsapp(activeWhatsapp - 1);
    }
    
    setDeleteConfirm({ open: false, name: null, index: null, isPredefined: false, type: null });
    toast.success(`"${name}" eliminado`);
  }, [deleteConfirm, customEmailNames, customWhatsappNames, emailContent, whatsappContent, assetsConfig, onAssetsConfigUpdate, emailNames, whatsappNames, activeEmail, activeWhatsapp]);

  // Open delete confirmation
  const openDeleteConfirm = useCallback((name, index, isPredefined, type) => {
    setDeleteConfirm({ open: true, name, index, isPredefined, type });
  }, []);

  // Persist email content to localStorage whenever it changes
  // CRITICAL: Only persist if we have a valid funnel ID to avoid cross-contamination
  useEffect(() => {
    if (funnel?.id && Object.keys(emailContent).length > 0) {
      localStorage.setItem(`sequences_emails_${funnel.id}`, JSON.stringify(emailContent));
      
      // Also sync to funnel.sequences in MongoDB for Dashboard to read
      // Use debounce to avoid too many API calls
      const syncToMongo = async () => {
        try {
          await api.put(`/funnels/${funnel.id}`, {
            sequences: {
              ...funnel?.sequences,
              emails: emailContent
            }
          });
        } catch (err) {
          console.log('Could not sync emails to funnel:', err.message);
        }
      };
      
      // Debounce: only sync after 2 seconds of no changes
      const timeoutId = setTimeout(syncToMongo, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [emailContent, funnel?.id, funnel?.sequences, api]);
  
  // Persist whatsapp content to localStorage whenever it changes
  useEffect(() => {
    if (funnel?.id && Object.keys(whatsappContent).length > 0) {
      localStorage.setItem(`sequences_whatsapp_${funnel.id}`, JSON.stringify(whatsappContent));
      
      // Also sync to funnel.sequences in MongoDB for Dashboard to read
      const syncToMongo = async () => {
        try {
          await api.put(`/funnels/${funnel.id}`, {
            sequences: {
              ...funnel?.sequences,
              whatsapp: whatsappContent
            }
          });
        } catch (err) {
          console.log('Could not sync whatsapp to funnel:', err.message);
        }
      };
      
      // Debounce: only sync after 2 seconds of no changes
      const timeoutId = setTimeout(syncToMongo, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [whatsappContent, funnel?.id, funnel?.sequences, api]);

  // Update from props when emailAssets changes (when funnel loads from DB)
  useEffect(() => {
    if (emailAssets && emailAssets.length > 0) {
      const contentMap = {};
      emailAssets.forEach((asset, index) => {
        if (asset.content?.copy) {
          contentMap[index] = asset.content.copy;
        }
      });
      if (Object.keys(contentMap).length > 0) {
        setEmailContent(prev => ({ ...prev, ...contentMap }));
      }
    }
  }, [emailAssets]);

  // Update from props when whatsappAssets changes (when funnel loads from DB)
  useEffect(() => {
    if (whatsappAssets && whatsappAssets.length > 0) {
      const contentMap = {};
      whatsappAssets.forEach((asset, index) => {
        if (asset.content?.copy) {
          contentMap[index] = asset.content.copy;
        }
      });
      if (Object.keys(contentMap).length > 0) {
        setWhatsappContent(prev => ({ ...prev, ...contentMap }));
      }
    }
  }, [whatsappAssets]);

  // Generate single email
  const generateEmail = async (index) => {
    // Emergency save if needed - block if we can't get a funnel ID
    const funnelId = await ensureFunnelExists();
    if (!funnelId) {
      return; // Error already shown by ensureFunnelExists
    }
    
    setEmailProgress(prev => ({ ...prev, [index]: 'generating' }));

    try {
      const emailName = customEmailNames[index];
      const metadata = getEmailMetadata(emailName);
      
      const response = await api.post('/ai/generate-copy', {
        funnel_id: funnelId,
        generation_type: 'email',
        asset_type: 'email',
        asset_name: emailName,
        section: `email_${index + 1}`,
        email_name: emailName,
        email_index: index + 1,
        total_emails: customEmailNames.length,
        brief: brief,
        // Metadata interna para el prompt
        momento_envio: metadata?.momento || '',
        objetivo: metadata?.objetivo || '',
        incluir: metadata?.incluir || '',
        excluir: metadata?.excluir || '',
        max_words: metadata?.max_words || 200,
      });

      setEmailContent(prev => ({
        ...prev,
        [index]: response.data.content,
      }));

      // Save asset to backend
      await api.post(`/funnels/${funnelId}/assets`, {
        funnel_id: funnelId,
        asset_type: 'email',
        content: {
          type: 'email',
          email_name: emailName,
          email_index: index + 1,
          copy: response.data.content,
        },
      });

      setEmailProgress(prev => ({ ...prev, [index]: 'completed' }));
      toast.success(`"${emailName}" generado`);
    } catch (error) {
      let errorMsg = 'Error desconocido';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (detail?.error) {
        errorMsg = detail.error;
      } else if (typeof detail === 'object') {
        errorMsg = JSON.stringify(detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      setEmailProgress(prev => ({ 
        ...prev, 
        [index]: 'error',
        [`${index}_error`]: errorMsg 
      }));
      toast.error('Error: ' + errorMsg);
    }
  };

  // Generate all emails
  const generateAllEmails = async () => {
    // Emergency save if needed - block if we can't get a funnel ID
    const funnelId = await ensureFunnelExists();
    if (!funnelId) {
      return; // Error already shown by ensureFunnelExists
    }

    setGeneratingAllEmails(true);

    for (let i = 0; i < customEmailNames.length; i++) {
      if (emailContent[i]) continue; // Skip if already generated

      setActiveEmail(i);
      setEmailProgress(prev => ({ ...prev, [i]: 'generating' }));

      try {
        const emailName = customEmailNames[i];
        const metadata = getEmailMetadata(emailName);
        
        const response = await api.post('/ai/generate-copy', {
          funnel_id: funnelId,
          generation_type: 'email',
          asset_type: 'email',
          asset_name: emailName,
          section: `email_${i + 1}`,
          email_name: emailName,
          email_index: i + 1,
          total_emails: customEmailNames.length,
          brief: brief,
          // Metadata interna para el prompt
          momento_envio: metadata?.momento || '',
          objetivo: metadata?.objetivo || '',
          incluir: metadata?.incluir || '',
          excluir: metadata?.excluir || '',
          max_words: metadata?.max_words || 200,
        });

        setEmailContent(prev => ({
          ...prev,
          [i]: response.data.content,
        }));

        // Save asset to backend immediately after each email
        await api.post(`/funnels/${funnelId}/assets`, {
          funnel_id: funnelId,
          asset_type: 'email',
          content: {
            type: 'email',
            email_name: emailName,
            email_index: i + 1,
            copy: response.data.content,
          },
        });

        setEmailProgress(prev => ({ ...prev, [i]: 'completed' }));
      } catch (error) {
        let errorMsg = 'Error desconocido';
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail?.error) {
          errorMsg = detail.error;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        setEmailProgress(prev => ({ 
          ...prev, 
          [i]: 'error',
          [`${i}_error`]: errorMsg
        }));
        // Continue with next email instead of stopping
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setGeneratingAllEmails(false);
    
    // Check how many succeeded
    const completedCount = Object.values(emailProgress).filter(v => v === 'completed').length + 
      Object.keys(emailContent).filter(k => emailContent[k]).length;
    const errorCount = customEmailNames.length - completedCount;
    
    if (errorCount > 0) {
      toast.warning(`${completedCount} emails generados. ${errorCount} con errores - usa "Reintentar" en cada uno.`);
    } else {
      toast.success('Secuencia de emails completada');
    }
  };

  // Generate single email (for retry or individual generation)
  const generateSingleEmail = async (index) => {
    const funnelId = await ensureFunnelExists();
    if (!funnelId) return;

    setEmailProgress(prev => ({ ...prev, [index]: 'generating' }));

    try {
      const emailName = customEmailNames[index];
      const metadata = getEmailMetadata(emailName);
      
      const response = await api.post('/ai/generate-copy', {
        funnel_id: funnelId,
        generation_type: 'email',
        asset_type: 'email',
        asset_name: emailName,
        section: `email_${index + 1}`,
        email_name: emailName,
        email_index: index + 1,
        total_emails: customEmailNames.length,
        brief: brief,
        // Metadata interna para el prompt
        momento_envio: metadata?.momento || '',
        objetivo: metadata?.objetivo || '',
        incluir: metadata?.incluir || '',
        excluir: metadata?.excluir || '',
        max_words: metadata?.max_words || 200,
      });

      setEmailContent(prev => ({
        ...prev,
        [index]: response.data.content,
      }));

      // Save asset to backend
      await api.post(`/funnels/${funnelId}/assets`, {
        funnel_id: funnelId,
        asset_type: 'email',
        content: {
          type: 'email',
          email_name: emailName,
          email_index: index + 1,
          copy: response.data.content,
        },
      });

      setEmailProgress(prev => ({ ...prev, [index]: 'completed' }));
      toast.success(`"${emailName}" generado correctamente`);
    } catch (error) {
      let errorMsg = 'Error desconocido';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (detail?.error) {
        errorMsg = detail.error;
      } else if (typeof detail === 'object') {
        errorMsg = JSON.stringify(detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setEmailProgress(prev => ({ 
        ...prev, 
        [index]: 'error',
        [`${index}_error`]: errorMsg
      }));
      toast.error(`Error: ${errorMsg}`);
    }
  };

  // Generate single WhatsApp
  const generateWhatsappMessage = async (index) => {
    // Emergency save if needed - block if we can't get a funnel ID
    const funnelId = await ensureFunnelExists();
    if (!funnelId) {
      return; // Error already shown by ensureFunnelExists
    }

    setWhatsappProgress(prev => ({ ...prev, [index]: 'generating' }));

    try {
      const messageName = customWhatsappNames[index];
      const metadata = getWhatsappMetadata(messageName);
      
      const response = await api.post('/ai/generate-copy', {
        funnel_id: funnelId,
        generation_type: 'whatsapp',
        asset_type: 'whatsapp',
        asset_name: messageName,
        section: `whatsapp_${index + 1}`,
        message_name: messageName,
        message_index: index + 1,
        total_messages: totalWhatsapp,
        brief: brief,
        // Metadata interna para el prompt
        momento_envio: metadata?.momento || '',
        objetivo: metadata?.objetivo || '',
        incluir: metadata?.incluir || '',
        excluir: metadata?.excluir || '',
        max_chars: metadata?.max_chars || 300,
      });

      setWhatsappContent(prev => ({
        ...prev,
        [index]: response.data.content,
      }));

      // Save asset to backend
      await api.post(`/funnels/${funnelId}/assets`, {
        funnel_id: funnelId,
        asset_type: 'whatsapp',
        content: {
          type: 'whatsapp',
          message_name: messageName,
          message_index: index + 1,
          copy: response.data.content,
        },
      });

      setWhatsappProgress(prev => ({ ...prev, [index]: 'completed' }));
      toast.success(`"${messageName}" generado`);
    } catch (error) {
      let errorMsg = 'Error desconocido';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (detail?.error) {
        errorMsg = detail.error;
      } else if (typeof detail === 'object') {
        errorMsg = JSON.stringify(detail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      setWhatsappProgress(prev => ({ ...prev, [index]: 'error', [`${index}_error`]: errorMsg }));
      toast.error('Error: ' + errorMsg);
    }
  };

  // Generate all WhatsApp
  const generateAllWhatsapp = async () => {
    // Emergency save if needed - block if we can't get a funnel ID
    const funnelId = await ensureFunnelExists();
    if (!funnelId) {
      return; // Error already shown by ensureFunnelExists
    }

    setGeneratingAllWhatsapp(true);

    for (let i = 0; i < totalWhatsapp; i++) {
      if (whatsappContent[i]) continue;

      setActiveWhatsapp(i);
      setWhatsappProgress(prev => ({ ...prev, [i]: 'generating' }));

      try {
        const messageName = customWhatsappNames[i];
        const metadata = getWhatsappMetadata(messageName);
        
        const response = await api.post('/ai/generate-copy', {
          funnel_id: funnelId,
          generation_type: 'whatsapp',
          asset_type: 'whatsapp',
          asset_name: messageName,
          section: `whatsapp_${i + 1}`,
          message_name: messageName,
          message_index: i + 1,
          total_messages: totalWhatsapp,
          brief: brief,
          // Metadata interna para el prompt
          momento_envio: metadata?.momento || '',
          objetivo: metadata?.objetivo || '',
          incluir: metadata?.incluir || '',
          excluir: metadata?.excluir || '',
          max_chars: metadata?.max_chars || 300,
        });

        setWhatsappContent(prev => ({
          ...prev,
          [i]: response.data.content,
        }));

        // Save asset to backend
        await api.post(`/funnels/${funnelId}/assets`, {
          funnel_id: funnelId,
          asset_type: 'whatsapp',
          content: {
            type: 'whatsapp',
            message_name: messageName,
            message_index: i + 1,
            copy: response.data.content,
          },
        });

        setWhatsappProgress(prev => ({ ...prev, [i]: 'completed' }));
      } catch (error) {
        let errorMsg = 'Error desconocido';
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail?.error) {
          errorMsg = detail.error;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        } else if (error.message) {
          errorMsg = error.message;
        }
        setWhatsappProgress(prev => ({ ...prev, [i]: 'error', [`${i}_error`]: errorMsg }));
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setGeneratingAllWhatsapp(false);
    toast.success('Secuencia de WhatsApp completada');
  };

  // Copy to clipboard
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2000);
    toast.success('Copiado');
  };

  // Export all content
  const exportContent = (type) => {
    const content = type === 'email' ? emailContent : whatsappContent;
    const names = type === 'email' ? emailNames : whatsappNames;
    
    let text = '';
    Object.keys(content).forEach(index => {
      text += `=== ${names[index]} ===\n\n${content[index]}\n\n---\n\n`;
    });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${funnel?.name || 'funnel'}-${type}-sequence.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportado');
  };

  // Get status icon
  const getStatusIcon = (progress, hasContent) => {
    if (progress === 'generating') {
      return <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />;
    }
    if (progress === 'completed' || hasContent) {
      return <CheckCircle2 className="w-4 h-4 text-[#00D68F]" />;
    }
    if (progress === 'error') {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  // Count completed items - only count those with actual content
  const completedEmails = Object.values(emailContent).filter(content => content && content.trim && content.trim().length > 0).length;
  const completedWhatsapp = Object.values(whatsappContent).filter(content => content && content.trim && content.trim().length > 0).length;
  
  // Count approved items
  const approvedEmailsCount = Object.keys(approvedEmails).length;
  const approvedWhatsappCount = Object.keys(approvedWhatsapp).length;

  return (
    <div data-testid="sequences-step" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-text-main mb-2">
          Secuencias de Comunicación
        </h2>
        <p className="text-text-secondary">
          Genera {totalEmails} emails y {totalWhatsapp} mensajes de WhatsApp personalizados
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-app-bg border border-border-subtle p-1">
          <TabsTrigger
            value="emails"
            className="data-[state=active]:bg-brand-500 data-[state=active]:text-white flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Emails
            <span className="ml-1 text-xs opacity-75">({completedEmails}/{totalEmails})</span>
          </TabsTrigger>
          <TabsTrigger
            value="whatsapp"
            className="data-[state=active]:bg-[#25D366] data-[state=active]:text-white flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            WhatsApp
            <span className="ml-1 text-xs opacity-75">({completedWhatsapp}/{totalWhatsapp})</span>
          </TabsTrigger>
        </TabsList>

        {/* Emails Tab */}
        <TabsContent value="emails" className="mt-6 space-y-4">
          {/* Message when strategy doesn't include emails */}
          {!strategyHasEmails && totalEmails === 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 text-center">
              <Mail className="w-12 h-12 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-blue-300 mb-2">
                Esta estrategia no incluye secuencia de emails
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                La estrategia "{funnel?.strategy_variant}" no requiere emails por defecto. 
                Puedes añadir emails manualmente si lo necesitas.
              </p>
              <Button
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                onClick={() => setShowAddEmailModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir Email Personalizado
              </Button>
            </div>
          )}
          
          {/* Normal content when has emails */}
          {(strategyHasEmails || totalEmails > 0) && (
            <>
          {/* Header with Generate All and Approve All buttons */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-text-secondary">
                Secuencia de {customEmailNames.length} emails para nurturing y conversión
              </p>
              <p className="text-xs text-text-muted mt-1">
                {approvedEmailsCount}/{completedEmails} aprobados
                {!strategyHasEmails && totalEmails > 0 && (
                  <span className="ml-2 text-blue-400">(añadidos manualmente)</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Approve All / Unapprove All Button */}
              {completedEmails > 0 && (
                approvedEmailsCount === completedEmails ? (
                  <Button
                    onClick={handleUnapproveAllEmails}
                    variant="outline"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    data-testid="unapprove-all-emails-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Desbloquear Todos
                  </Button>
                ) : (
                  <Button
                    onClick={handleApproveAllEmails}
                    disabled={completedEmails === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="approve-all-emails-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Aprobar Todos ({completedEmails - approvedEmailsCount})
                  </Button>
                )
              )}
              <Button
                onClick={generateAllEmails}
                disabled={generatingAllEmails || completedEmails === totalEmails}
                className="bg-gradient-to-r from-brand to-purple-600 hover:from-brand-hover hover:to-purple-700 text-white"
              >
                {generatingAllEmails ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando {completedEmails + 1}/{customEmailNames.length}...
                  </>
                ) : completedEmails === customEmailNames.length ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Todo Generado
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generar Todo ({customEmailNames.length - completedEmails})
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Progreso</span>
              <span className="text-text-main font-medium">{completedEmails}/{customEmailNames.length} emails</span>
            </div>
            <div className="h-2 bg-app-bg rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-brand to-purple-600 transition-all duration-500"
                style={{ width: `${(completedEmails / customEmailNames.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Main content: 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left: Email list with D&D */}
            <div className="lg:col-span-1 space-y-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-text-muted uppercase tracking-wider">
                  Emails a generar
                </h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAddEmailModal(true)}
                  className="h-7 px-2 text-brand-500 hover:text-brand-hover hover:bg-brand/10"
                  data-testid="add-email-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir
                </Button>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEmailDragEnd}>
                <SortableContext items={customEmailNames.map((_, i) => `email-${i}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
                    {customEmailNames.map((name, index) => (
                      <SortableSequenceItem
                        key={`email-${index}`}
                        id={`email-${index}`}
                        name={name}
                        index={index}
                        isActive={activeEmail === index}
                        hasContent={!!emailContent[index]}
                        isPredefined={isPredefinedEmail(name)}
                        isGeneratingAll={generatingAllEmails}
                        onSelect={setActiveEmail}
                        onDelete={(n, i, p) => openDeleteConfirm(n, i, p, 'email')}
                        getStatusIcon={(idx, hasContent) => getStatusIcon(emailProgress[idx], hasContent)}
                        type="email"
                        momento={getEmailMetadata(name)?.momento}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            {/* Right: Content area */}
            <div className="lg:col-span-3">
              <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-app-bg/50">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      emailContent[activeEmail] ? 'bg-[#00D68F]/10' : 'bg-brand/10'
                    }`}>
                      <Mail className={`w-5 h-5 ${emailContent[activeEmail] ? 'text-[#00D68F]' : 'text-brand'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-main">{customEmailNames[activeEmail]}</h3>
                      <p className="text-xs text-text-muted">
                        Email {activeEmail + 1} de {customEmailNames.length} • {emailContent[activeEmail] ? 'Generado ✓' : 'Pendiente'}
                      </p>
                      {/* Metadata del email */}
                      {(() => {
                        const metadata = getEmailMetadata(customEmailNames[activeEmail]);
                        if (!metadata) return null;
                        return (
                          <div className="flex items-center gap-4 mt-1">
                            {metadata.momento && (
                              <span className="text-xs text-text-muted flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {metadata.momento}
                              </span>
                            )}
                            {metadata.max_words && (
                              <span className="text-xs text-text-muted">
                                ~{metadata.max_words} palabras
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => generateEmail(activeEmail)}
                      disabled={emailProgress[activeEmail] === 'generating' || generatingAllEmails || approvedEmails[activeEmail]}
                      className="bg-brand-500 hover:bg-brand-hover text-white"
                    >
                      {emailProgress[activeEmail] === 'generating' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generando...
                        </>
                      ) : emailContent[activeEmail] ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerar
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generar
                        </>
                      )}
                    </Button>
                    
                    {emailContent[activeEmail] && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(emailContent[activeEmail], `email-${activeEmail}`)}
                        className="border-border-subtle"
                      >
                        {copiedItem === `email-${activeEmail}` ? (
                          <Check className="w-4 h-4 text-[#00D68F]" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    
                    {/* Approve/Unapprove Button */}
                    {emailContent[activeEmail] && (
                      approvedEmails[activeEmail] ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnapproveEmail(activeEmail)}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <LockOpen className="w-4 h-4 mr-1" />
                          Desbloquear
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleApproveEmail(activeEmail)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                      )
                    )}
                  </div>
                </div>
                
                {/* Approved Banner */}
                {approvedEmails[activeEmail] && (
                  <div className="mx-6 mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">Email aprobado - Modo solo lectura</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnapproveEmail(activeEmail)}
                      className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                    >
                      <LockOpen className="w-3 h-3 mr-1" />
                      Desbloquear para editar
                    </Button>
                  </div>
                )}

                <div className="p-6 min-h-[350px] max-h-[450px] overflow-y-auto">
                  {emailProgress[activeEmail] === 'generating' ? (
                    <div className="flex flex-col items-center justify-center h-[300px]">
                      <div className="w-16 h-16 rounded-2xl bg-brand/20 flex items-center justify-center mb-4 animate-pulse">
                        <Mail className="w-8 h-8 text-brand-500" />
                      </div>
                      <p className="text-text-main font-medium">Generando email con IA...</p>
                    </div>
                  ) : emailProgress[activeEmail] === 'error' ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-4">
                        <AlertCircle className="w-8 h-8 text-red-400" />
                      </div>
                      <h3 className="text-lg font-medium text-text-main mb-2">Error al generar</h3>
                      <p className="text-text-secondary max-w-md mb-4">
                        {typeof emailProgress[`${activeEmail}_error`] === 'object' 
                          ? (emailProgress[`${activeEmail}_error`]?.message || emailProgress[`${activeEmail}_error`]?.detail || JSON.stringify(emailProgress[`${activeEmail}_error`]))
                          : (emailProgress[`${activeEmail}_error`] || 'Ocurrió un error al generar este email')}
                      </p>
                      <Button
                        onClick={() => generateEmail(activeEmail)}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid={`retry-email-${activeEmail}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reintentar
                      </Button>
                    </div>
                  ) : emailContent[activeEmail] ? (
                    // Check if in editing mode
                    editingEmail === activeEmail ? (
                      <div className="space-y-4">
                        <textarea
                          value={editedEmailContent}
                          onChange={(e) => setEditedEmailContent(e.target.value)}
                          className="w-full h-[300px] p-4 rounded-lg bg-app-bg border border-border-subtle text-text-main resize-none focus:border-brand-500 focus:outline-none font-mono text-sm"
                          placeholder="Edita el contenido del email..."
                          data-testid={`edit-email-textarea-${activeEmail}`}
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={handleCancelEditingEmail}
                            className="border-border-subtle text-text-muted"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                          <Button
                            onClick={() => handleSaveEditedEmail(activeEmail)}
                            className="bg-brand-500 hover:bg-brand-hover text-white"
                            data-testid={`save-email-btn-${activeEmail}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Guardar Cambios
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Read-only view with edit button when not approved
                      <div className="relative">
                        {!approvedEmails[activeEmail] && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartEditingEmail(activeEmail)}
                            className="absolute top-0 right-0 border-brand/30 text-brand-500 hover:bg-brand/10"
                            data-testid={`edit-email-btn-${activeEmail}`}
                          >
                            <Wand2 className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                        )}
                        <div className="prose prose-invert prose-sm max-w-none">
                          {cleanContentLabels(emailContent[activeEmail]).split('\n').map((line, i) => {
                            // Clean markdown formatting
                            const cleanLine = line
                              .replace(/\*\*(.+?)\*\*/g, '$1')
                              .replace(/\*(.+?)\*/g, '$1')
                              .replace(/^#+\s*/, '');
                            
                            // Use content-based key for better React reconciliation
                            const lineKey = `line-${i}-${cleanLine.slice(0, 20).replace(/\s/g, '')}`;
                            
                            if (line.toLowerCase().startsWith('asunto:') || line.toLowerCase().startsWith('subject:')) {
                              return <p key={lineKey} className="font-bold text-brand-500">{cleanLine}</p>;
                            }
                            if (line.startsWith('#')) {
                              return <h3 key={lineKey} className="text-lg font-bold text-text-main mt-4">{cleanLine}</h3>;
                            }
                            if (line.trim() === '') return <br key={`br-${i}`} />;
                            return <p key={lineKey} className="text-text-secondary leading-relaxed">{cleanLine}</p>;
                          })}
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <Mail className="w-12 h-12 text-text-muted mb-4" />
                      <h3 className="text-lg font-medium text-text-main mb-2">Sin contenido generado</h3>
                      <p className="text-text-secondary max-w-md">
                        Haz clic en "Generar" para crear el email "{customEmailNames[activeEmail]}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {completedEmails > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => exportContent('email')}
                className="border-border-subtle text-text-secondary"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Todos
              </Button>
            </div>
          )}
            </>
          )}
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp" className="mt-6 space-y-4">
          {/* Message when strategy doesn't include WhatsApp */}
          {!strategyHasWhatsapp && totalWhatsapp === 0 && (
            <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-6 text-center">
              <Phone className="w-12 h-12 text-[#25D366] mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-[#25D366] mb-2">
                Esta estrategia no incluye secuencia de WhatsApp
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                La estrategia "{funnel?.strategy_variant}" no requiere WhatsApp por defecto. 
                Puedes añadir mensajes manualmente si lo necesitas.
              </p>
              <Button
                variant="outline"
                className="border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10"
                onClick={() => setShowAddWhatsappModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Añadir WhatsApp Personalizado
              </Button>
            </div>
          )}
          
          {/* Normal content when has WhatsApp */}
          {(strategyHasWhatsapp || totalWhatsapp > 0) && (
            <>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-text-secondary">
                Secuencia de {totalWhatsapp} mensajes de WhatsApp
              </p>
              <p className="text-xs text-text-muted mt-1">
                {approvedWhatsappCount}/{completedWhatsapp} aprobados
                {!strategyHasWhatsapp && totalWhatsapp > 0 && (
                  <span className="ml-2 text-[#25D366]">(añadidos manualmente)</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Approve All / Unapprove All Button */}
              {completedWhatsapp > 0 && (
                approvedWhatsappCount === completedWhatsapp ? (
                  <Button
                    onClick={handleUnapproveAllWhatsapp}
                    variant="outline"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    data-testid="unapprove-all-whatsapp-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Desbloquear Todos
                  </Button>
                ) : (
                  <Button
                    onClick={handleApproveAllWhatsapp}
                    disabled={completedWhatsapp === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="approve-all-whatsapp-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Aprobar Todos ({completedWhatsapp - approvedWhatsappCount})
                  </Button>
                )
              )}
              <Button
                onClick={generateAllWhatsapp}
                disabled={generatingAllWhatsapp || completedWhatsapp === totalWhatsapp}
                className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#20BD5C] hover:to-[#0F7A6B] text-white"
              >
                {generatingAllWhatsapp ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando {completedWhatsapp + 1}/{totalWhatsapp}...
                  </>
                ) : completedWhatsapp === totalWhatsapp ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Todo Generado
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generar Todo ({totalWhatsapp - completedWhatsapp})
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Progreso</span>
              <span className="text-text-main font-medium">{completedWhatsapp}/{totalWhatsapp} mensajes</span>
            </div>
            <div className="h-2 bg-app-bg rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#25D366] to-[#128C7E] transition-all duration-500"
                style={{ width: `${(completedWhatsapp / totalWhatsapp) * 100}%` }}
              />
            </div>
          </div>

          {/* Main content: 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left: WhatsApp list with D&D */}
            <div className="lg:col-span-1 space-y-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-text-muted uppercase tracking-wider">
                  Mensajes a generar
                </h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAddWhatsappModal(true)}
                  className="h-7 px-2 text-[#25D366] hover:text-[#128C7E] hover:bg-[#25D366]/10"
                  data-testid="add-whatsapp-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir
                </Button>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleWhatsappDragEnd}>
                <SortableContext items={customWhatsappNames.map((_, i) => `whatsapp-${i}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
                    {customWhatsappNames.map((name, index) => (
                      <SortableSequenceItem
                        key={`whatsapp-${index}`}
                        id={`whatsapp-${index}`}
                        name={name}
                        index={index}
                        isActive={activeWhatsapp === index}
                        hasContent={!!whatsappContent[index]}
                        isPredefined={isPredefinedWhatsapp(name)}
                        isGeneratingAll={generatingAllWhatsapp}
                        onSelect={setActiveWhatsapp}
                        onDelete={(n, i, p) => openDeleteConfirm(n, i, p, 'whatsapp')}
                        getStatusIcon={(idx, hasContent) => getStatusIcon(whatsappProgress[idx], hasContent)}
                        type="whatsapp"
                        momento={getWhatsappMetadata(name)?.momento}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            {/* Right: Content area */}
            <div className="lg:col-span-3">
              <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border-subtle bg-app-bg/50">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      whatsappContent[activeWhatsapp] ? 'bg-[#00D68F]/10' : 'bg-[#25D366]/10'
                    }`}>
                      <MessageCircle className={`w-5 h-5 ${whatsappContent[activeWhatsapp] ? 'text-[#00D68F]' : 'text-[#25D366]'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-main">{customWhatsappNames[activeWhatsapp]}</h3>
                      <p className="text-xs text-text-muted">
                        Mensaje {activeWhatsapp + 1} de {customWhatsappNames.length}
                      </p>
                      {/* Metadata del mensaje */}
                      {(() => {
                        const metadata = getWhatsappMetadata(customWhatsappNames[activeWhatsapp]);
                        if (!metadata) return null;
                        return (
                          <div className="flex items-center gap-4 mt-1">
                            {metadata.momento && (
                              <span className="text-xs text-text-muted flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {metadata.momento}
                              </span>
                            )}
                            {metadata.max_chars && (
                              <span className="text-xs text-text-muted">
                                Máx. {metadata.max_chars} caracteres
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => generateWhatsappMessage(activeWhatsapp)}
                      disabled={whatsappProgress[activeWhatsapp] === 'generating' || generatingAllWhatsapp || approvedWhatsapp[activeWhatsapp]}
                      className="bg-[#25D366] hover:bg-[#128C7E] text-white"
                    >
                      {whatsappProgress[activeWhatsapp] === 'generating' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generando...
                        </>
                      ) : whatsappContent[activeWhatsapp] ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regenerar
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generar
                        </>
                      )}
                    </Button>
                    
                    {whatsappContent[activeWhatsapp] && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(whatsappContent[activeWhatsapp], `wa-${activeWhatsapp}`)}
                        className="border-border-subtle"
                      >
                        {copiedItem === `wa-${activeWhatsapp}` ? (
                          <Check className="w-4 h-4 text-[#00D68F]" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    
                    {/* Approve/Unapprove Button for WhatsApp */}
                    {whatsappContent[activeWhatsapp] && (
                      approvedWhatsapp[activeWhatsapp] ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnapproveWhatsapp(activeWhatsapp)}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <LockOpen className="w-4 h-4 mr-1" />
                          Desbloquear
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleApproveWhatsapp(activeWhatsapp)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aprobar
                        </Button>
                      )
                    )}
                  </div>
                </div>
                
                {/* Approved Banner for WhatsApp */}
                {approvedWhatsapp[activeWhatsapp] && (
                  <div className="mx-6 mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">WhatsApp aprobado - Modo solo lectura</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnapproveWhatsapp(activeWhatsapp)}
                      className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                    >
                      <LockOpen className="w-3 h-3 mr-1" />
                      Desbloquear para editar
                    </Button>
                  </div>
                )}

                <div className="p-6 min-h-[350px] max-h-[450px] overflow-y-auto">
                  {whatsappProgress[activeWhatsapp] === 'generating' ? (
                    <div className="flex flex-col items-center justify-center h-[300px]">
                      <div className="w-16 h-16 rounded-2xl bg-[#25D366]/20 flex items-center justify-center mb-4 animate-pulse">
                        <MessageCircle className="w-8 h-8 text-[#25D366]" />
                      </div>
                      <p className="text-text-main font-medium">Generando mensaje...</p>
                    </div>
                  ) : whatsappProgress[activeWhatsapp] === 'error' ? (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-4">
                        <AlertCircle className="w-8 h-8 text-red-400" />
                      </div>
                      <h3 className="text-lg font-medium text-text-main mb-2">Error al generar</h3>
                      <p className="text-text-secondary max-w-md mb-4">
                        {typeof whatsappProgress[`${activeWhatsapp}_error`] === 'object' 
                          ? JSON.stringify(whatsappProgress[`${activeWhatsapp}_error`])
                          : (whatsappProgress[`${activeWhatsapp}_error`] || 'Ocurrió un error al generar este mensaje')}
                      </p>
                      <Button
                        onClick={() => generateWhatsappMessage(activeWhatsapp)}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reintentar
                      </Button>
                    </div>
                  ) : whatsappContent[activeWhatsapp] ? (
                    // Check if in editing mode
                    editingWhatsapp === activeWhatsapp ? (
                      <div className="space-y-4">
                        <textarea
                          value={editedWhatsappContent}
                          onChange={(e) => setEditedWhatsappContent(e.target.value)}
                          className="w-full h-[200px] p-4 rounded-lg bg-app-bg border border-border-subtle text-text-main resize-none focus:border-[#25D366] focus:outline-none font-mono text-sm"
                          placeholder="Edita el mensaje de WhatsApp..."
                          maxLength={500}
                          data-testid={`edit-whatsapp-textarea-${activeWhatsapp}`}
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-text-muted">
                            {editedWhatsappContent.length}/500 caracteres
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={handleCancelEditingWhatsapp}
                              className="border-border-subtle text-text-muted"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                            <Button
                              onClick={() => handleSaveEditedWhatsapp(activeWhatsapp)}
                              className="bg-[#25D366] hover:bg-[#20BD5C] text-white"
                              data-testid={`save-whatsapp-btn-${activeWhatsapp}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Guardar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Read-only view with edit button when not approved
                      <div className="relative">
                        {!approvedWhatsapp[activeWhatsapp] && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStartEditingWhatsapp(activeWhatsapp)}
                            className="absolute top-0 right-0 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10"
                            data-testid={`edit-whatsapp-btn-${activeWhatsapp}`}
                          >
                            <Wand2 className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                        )}
                        <div className="bg-[#075E54] rounded-2xl rounded-tl-none p-4 max-w-md">
                          <p className="text-white whitespace-pre-wrap">{cleanContentLabels(whatsappContent[activeWhatsapp])}</p>
                          <p className="text-white/50 text-xs text-right mt-2">✓✓ 10:30</p>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <MessageCircle className="w-12 h-12 text-text-muted mb-4" />
                      <h3 className="text-lg font-medium text-text-main mb-2">Sin mensaje generado</h3>
                      <p className="text-text-secondary max-w-md">
                        Haz clic en "Generar" para crear "{whatsappNames[activeWhatsapp]}"
                      </p>
                    </div>
                  )}
                </div>

                {whatsappContent[activeWhatsapp] && (
                  <div className="px-6 py-3 border-t border-border-subtle bg-[#25D366]/5">
                    <p className="text-text-muted text-sm">
                      💡 Mensaje optimizado para WhatsApp (máx. 500 caracteres)
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {completedWhatsapp > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => exportContent('whatsapp')}
                className="border-border-subtle text-text-secondary"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Todos
              </Button>
            </div>
          )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ====== ADD EMAIL MODAL ====== */}
      <Dialog open={showAddEmailModal} onOpenChange={setShowAddEmailModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Añadir Nuevo Email</DialogTitle>
            <DialogDescription className="text-text-secondary">
              Introduce un nombre descriptivo para el nuevo email de la secuencia.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Ej: Email de Seguimiento, Recordatorio Especial..."
              className="bg-app-bg border-border-subtle text-text-main"
              onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
              data-testid="new-email-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowAddEmailModal(false); setNewItemName(''); }} className="text-text-muted">
              Cancelar
            </Button>
            <Button onClick={handleAddEmail} className="bg-brand-500 hover:bg-brand-hover text-white" data-testid="confirm-add-email-btn">
              <Plus className="w-4 h-4 mr-2" />
              Añadir Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== ADD WHATSAPP MODAL ====== */}
      <Dialog open={showAddWhatsappModal} onOpenChange={setShowAddWhatsappModal}>
        <DialogContent className="bg-surface border-border-subtle">
          <DialogHeader>
            <DialogTitle className="text-text-main">Añadir Nuevo WhatsApp</DialogTitle>
            <DialogDescription className="text-text-secondary">
              Introduce un nombre descriptivo para el nuevo mensaje de WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Ej: WA Promoción, WA Recordatorio Final..."
              className="bg-app-bg border-border-subtle text-text-main"
              onKeyDown={(e) => e.key === 'Enter' && handleAddWhatsapp()}
              data-testid="new-whatsapp-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowAddWhatsappModal(false); setNewItemName(''); }} className="text-text-muted">
              Cancelar
            </Button>
            <Button onClick={handleAddWhatsapp} className="bg-[#25D366] hover:bg-[#128C7E] text-white" data-testid="confirm-add-whatsapp-btn">
              <Plus className="w-4 h-4 mr-2" />
              Añadir Mensaje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== DELETE CONFIRMATION ALERT ====== */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, name: null, index: null, isPredefined: false, type: null })}>
        <AlertDialogContent className="bg-surface border-border-subtle">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-text-main">
              {deleteConfirm.isPredefined ? '¿Eliminar pieza predefinida?' : `¿Eliminar ${deleteConfirm.type === 'email' ? 'email' : 'mensaje'}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary">
              {deleteConfirm.isPredefined ? (
                <>
                  <strong className="text-amber-400">"{deleteConfirm.name}"</strong> es una pieza predefinida por la estrategia.
                  <br /><br />
                  Si la eliminas, se borrará todo su contenido generado.
                </>
              ) : (
                <>
                  ¿Estás seguro de que quieres eliminar <strong className="text-text-main">"{deleteConfirm.name}"</strong>?
                  <br /><br />
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border-subtle text-text-muted hover:text-text-main hover:bg-surface">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-sequence-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SequencesStep;
